import type {
  CameraState,
  MiniMapController,
  MiniMapOptions,
  SelectionState,
  ViewerConfig,
  ViewerEventHandler,
  ViewerEventMap,
  ViewerEventName,
  ViewerGraph,
  ViewerOperationResult,
  ViewerOptions,
  ViewerSnapshot,
  ViewerStats,
  ViewerThemeTokens,
  ViewportInsets,
  ViewportSnapshot,
  VisibleNode,
} from "./contracts"
import { GraphCanvas } from "./internal/GraphCanvas"
import { MiniMapCanvas } from "./internal/MiniMapCanvas"
import type {
  CanvasEdge,
  CanvasNode,
  GraphCanvasConfig,
} from "./internal/types"
import { defaultConfig, defaultTheme } from "./theme"

export const VIEWER_MIN_SCALE = 0.02
export const VIEWER_MAX_SCALE = 3

export interface ViewerController {
  setGraph(graph: ViewerGraph): void
  setSelection(id: string | null): void
  setHover(id: string | null): void
  setHighlights(ids: Iterable<string> | null): void
  setAttention(ids: Iterable<string> | null): void
  setVisibleEdgeKinds(kinds: Iterable<string> | null): void
  setConfig(config: Partial<ViewerConfig>): void
  setTheme(theme: Partial<ViewerThemeTokens>): void
  setViewportInsets(insets: ViewportInsets, animate?: boolean): void
  fitView(
    animate?: boolean,
    signal?: AbortSignal,
  ): Promise<ViewerOperationResult>
  resetView(animate?: boolean): void
  focusNode(id: string, animate?: boolean): boolean
  centerOn(x: number, y: number, animate?: boolean): void
  zoomIn(animate?: boolean): void
  zoomOut(animate?: boolean): void
  zoomBy(factor: number, animate?: boolean): void
  screenToWorld(x: number, y: number): { x: number; y: number }
  worldToScreen(x: number, y: number): { x: number; y: number }
  getCameraState(): Readonly<CameraState>
  getViewport(): ViewportSnapshot
  getVisibleNodes(): readonly VisibleNode[]
  getSnapshot(): ViewerSnapshot
  getStats(): ViewerStats
  subscribe<K extends ViewerEventName>(
    event: K,
    handler: ViewerEventHandler<K>,
  ): () => void
  dispose(): void
}

type ListenerMap = {
  [K in ViewerEventName]?: Set<ViewerEventHandler<K>>
}

const instances = new WeakMap<ViewerController, ViewerImplementation>()

export function createViewer(
  container: HTMLElement,
  options: ViewerOptions = {},
): ViewerController {
  return registerViewer(new ViewerImplementation(container, options))
}

export function createViewerForTest(
  container: HTMLElement,
  options: ViewerOptions,
  engine: GraphCanvas,
): ViewerController {
  return registerViewer(new ViewerImplementation(container, options, engine))
}

function registerViewer(viewer: ViewerImplementation) {
  instances.set(viewer, viewer)
  return viewer
}

export function createMiniMap(
  container: HTMLElement,
  viewer: ViewerController,
  options: MiniMapOptions = {},
): MiniMapController {
  const implementation = instances.get(viewer)
  if (!implementation) {
    throw new Error(
      "MiniMap requires a ViewerController created by createViewer",
    )
  }
  return implementation.createMiniMap(container, options)
}

class ViewerImplementation implements ViewerController {
  private readonly listeners: ListenerMap = {}
  private graph: ViewerGraph = { nodes: [], edges: [] }
  private selectedId: string | null = null
  private hoveredId: string | null = null
  private theme: ViewerThemeTokens = { ...defaultTheme }
  private disposed = false
  private lastViewportKey = ""
  private readonly minimaps = new Set<MiniMapCanvas>()

  constructor(
    container: HTMLElement,
    options: ViewerOptions,
    private readonly engine = new GraphCanvas(),
  ) {
    this.theme = { ...this.theme, ...options.theme }
    this.engine.setConfig(toInternalConfig(defaultConfig, this.theme))
    this.engine.mount(container, {
      onNodeClick: (id) => {
        this.selectedId = id
        this.emit("selection", { ...this.selection(), source: "interaction" })
      },
      onNodeHover: (id) => {
        this.hoveredId = id
        this.emit("hover", { ...this.selection(), source: "interaction" })
      },
      onPaneClick: () => {
        this.selectedId = null
        this.emit("selection", { ...this.selection(), source: "interaction" })
      },
      onRender: () => this.onRender(),
    })
    this.engine.onFps(() => this.emit("stats", this.getStats()))
    if (options.config) this.setConfig(options.config)
    if (options.graph) this.setGraph(options.graph)
    if (options.selectedId !== undefined) this.setSelection(options.selectedId)
    this.setHighlights(options.highlightedIds ?? null)
    this.setAttention(options.attentionIds ?? null)
    this.setVisibleEdgeKinds(options.visibleEdgeKinds ?? null)
  }

  setGraph(graph: ViewerGraph) {
    if (this.disposed) return
    this.graph = normalizeGraph(graph)
    this.engine.setData(
      this.graph.nodes.map((node) => ({
        ...node,
        spec: { ...(node.spec ?? {}) },
      })) as CanvasNode[],
      this.graph.edges.map((edge) => ({
        ...edge,
        dash: edge.dash ? [...edge.dash] : undefined,
      })) as CanvasEdge[],
    )
    for (const minimap of this.minimaps)
      minimap.setScene(this.engine.getScene())
    this.emit("graph", this.graph)
  }

  setSelection(id: string | null) {
    if (this.disposed) return
    const changed = id !== this.selectedId
    this.selectedId = id
    this.engine.setSelectedNode(id)
    if (changed) {
      this.emit("selection", { ...this.selection(), source: "programmatic" })
    }
  }

  setHover(id: string | null) {
    if (this.disposed) return
    const changed = id !== this.hoveredId
    this.hoveredId = id
    this.engine.setHoveredNode(id)
    if (changed) {
      this.emit("hover", { ...this.selection(), source: "programmatic" })
    }
  }

  setHighlights(ids: Iterable<string> | null) {
    if (!this.disposed) this.engine.setHighlight(copySet(ids))
  }

  setAttention(ids: Iterable<string> | null) {
    if (!this.disposed) this.engine.setAttention(copySet(ids))
  }

  setVisibleEdgeKinds(kinds: Iterable<string> | null) {
    if (!this.disposed) this.engine.setVisibleEdgeKinds(copySet(kinds))
  }

  setConfig(config: Partial<ViewerConfig>) {
    if (this.disposed) return
    this.engine.setConfig(config)
  }

  setTheme(theme: Partial<ViewerThemeTokens>) {
    if (this.disposed) return
    this.theme = { ...this.theme, ...theme }
    this.engine.setConfig(toInternalTheme(this.theme))
    for (const minimap of this.minimaps) {
      minimap.setTheme(toMiniMapTheme(this.theme, {}))
    }
  }

  setViewportInsets(insets: ViewportInsets, animate = true) {
    if (!this.disposed) this.engine.setViewportInsets({ ...insets }, animate)
  }

  fitView(animate = true, signal?: AbortSignal) {
    return this.disposed
      ? Promise.resolve("unavailable" as const)
      : this.engine.fitView(animate, signal)
  }

  resetView(animate = true) {
    if (!this.disposed) this.engine.resetView(animate)
  }

  focusNode(id: string, animate = true) {
    return !this.disposed && this.engine.focusNode(id, animate)
  }

  centerOn(x: number, y: number, animate = false) {
    if (!this.disposed) this.engine.centerOn(x, y, animate)
  }

  zoomIn(animate = true) {
    if (!this.disposed) this.engine.zoomIn(animate)
  }

  zoomOut(animate = true) {
    if (!this.disposed) this.engine.zoomOut(animate)
  }

  zoomBy(factor: number, animate = true) {
    if (!this.disposed) this.engine.zoomBy(factor, animate)
  }

  screenToWorld(x: number, y: number) {
    return this.engine.screenToWorld(x, y)
  }

  worldToScreen(x: number, y: number) {
    return this.engine.worldToScreen(x, y)
  }

  getCameraState() {
    return Object.freeze({ ...this.engine.getCameraState() })
  }

  getViewport(): ViewportSnapshot {
    return Object.freeze({
      width: this.engine.getWidth(),
      height: this.engine.getHeight(),
      camera: this.getCameraState(),
      visibleWorld: Object.freeze({ ...this.engine.getVisibleRect() }),
    })
  }

  getVisibleNodes(): readonly VisibleNode[] {
    const graphById = new Map(this.graph.nodes.map((node) => [node.id, node]))
    return Object.freeze(
      this.engine.getVisibleNodes().map((positioned) => {
        const node = graphById.get(positioned.id) ?? positioned
        const topLeft = this.worldToScreen(positioned.x, positioned.y)
        const camera = this.engine.getCameraState()
        return Object.freeze({
          ...node,
          spec: node.spec,
          screenX: topLeft.x,
          screenY: topLeft.y,
          screenWidth: positioned.width * camera.scale,
          screenHeight: positioned.height * camera.scale,
        }) as VisibleNode
      }),
    )
  }

  getSnapshot(): ViewerSnapshot {
    return Object.freeze({
      graph: this.graph,
      viewport: this.getViewport(),
      selection: Object.freeze(this.selection()),
      visibleNodes: this.getVisibleNodes(),
    })
  }

  getStats() {
    const stats = this.engine.getDebugStats()
    return Object.freeze({
      ...stats,
      timings: Object.freeze({ ...stats.timings }),
    })
  }

  subscribe<K extends ViewerEventName>(
    event: K,
    handler: ViewerEventHandler<K>,
  ) {
    if (this.disposed) return () => {}
    const set = (this.listeners[event] ??= new Set() as never) as Set<
      ViewerEventHandler<K>
    >
    set.add(handler)
    return () => set.delete(handler)
  }

  createMiniMap(container: HTMLElement, options: MiniMapOptions) {
    const minimap = new MiniMapCanvas(toMiniMapTheme(this.theme, options))
    minimap.mount(container)
    minimap.setScene(this.engine.getScene())
    this.minimaps.add(minimap)
    minimap.setNavigateHandler((x, y) => this.centerOn(x, y))
    const unsubscribe = this.subscribe("viewport", () => {
      minimap.render(
        this.engine.camera,
        this.engine.getWidth(),
        this.engine.getHeight(),
      )
    })
    let disposed = false
    return {
      setHighlights: (ids: Iterable<string> | null) =>
        minimap.setHighlight(copySet(ids)),
      setTheme: (theme: MiniMapOptions["theme"]) =>
        minimap.setTheme(toMiniMapTheme(this.theme, { theme })),
      dispose: () => {
        if (disposed) return
        disposed = true
        unsubscribe()
        this.minimaps.delete(minimap)
        minimap.unmount()
      },
    }
  }

  dispose() {
    if (this.disposed) return
    this.disposed = true
    for (const minimap of this.minimaps) minimap.unmount()
    this.minimaps.clear()
    this.engine.unmount()
    this.emit("dispose", undefined)
    for (const set of Object.values(this.listeners)) set?.clear()
    instances.delete(this)
  }

  private selection(): SelectionState {
    return { selectedId: this.selectedId, hoveredId: this.hoveredId }
  }

  private onRender() {
    const viewport = this.getViewport()
    const key = `${viewport.width}:${viewport.height}:${viewport.camera.x}:${viewport.camera.y}:${viewport.camera.scale}`
    if (key !== this.lastViewportKey) {
      this.lastViewportKey = key
      this.emit("viewport", viewport)
    }
    if (this.listeners.render?.size) this.emit("render", this.getSnapshot())
  }

  private emit<K extends ViewerEventName>(event: K, value: ViewerEventMap[K]) {
    const set = this.listeners[event] as Set<ViewerEventHandler<K>> | undefined
    if (!set) return
    for (const handler of [...set]) handler(value)
  }
}

export function normalizeGraph(graph: ViewerGraph): ViewerGraph {
  return Object.freeze({
    nodes: Object.freeze(
      [...graph.nodes].map((node) =>
        Object.freeze({
          ...node,
          spec: node.spec ? cloneAndFreeze(node.spec) : undefined,
        }),
      ),
    ),
    edges: Object.freeze(
      [...graph.edges].map((edge) =>
        Object.freeze({
          ...edge,
          dash: edge.dash ? Object.freeze([...edge.dash]) : undefined,
        }),
      ),
    ),
  })
}

function cloneAndFreeze<T>(
  value: T,
  seen = new WeakMap<object, unknown>(),
  depth = 0,
): T {
  if (value === null || typeof value !== "object") return value
  if (depth > 100) throw new RangeError("Viewer spec exceeds 100 nested levels")

  const existing = seen.get(value)
  if (existing !== undefined) return existing as T

  if (Array.isArray(value)) {
    const clone: unknown[] = []
    seen.set(value, clone)
    for (const item of value) {
      clone.push(cloneAndFreeze(item, seen, depth + 1))
    }
    return Object.freeze(clone) as T
  }

  const prototype = Object.getPrototypeOf(value)
  if (prototype !== Object.prototype && prototype !== null) {
    throw new TypeError("Viewer specs support only plain objects and arrays")
  }

  const clone: Record<string, unknown> = Object.create(prototype)
  seen.set(value, clone)
  for (const key of Object.keys(value)) {
    clone[key] = cloneAndFreeze(
      (value as Record<string, unknown>)[key],
      seen,
      depth + 1,
    )
  }
  return Object.freeze(clone) as T
}

function copySet(values: Iterable<string> | null | undefined) {
  return values == null ? null : new Set(values)
}

function toInternalConfig(
  config: Readonly<ViewerConfig>,
  theme: ViewerThemeTokens,
): GraphCanvasConfig {
  return { ...config, ...toInternalTheme(theme) }
}

function toInternalTheme(theme: ViewerThemeTokens) {
  return {
    background: theme.background,
    gridColor: theme.grid,
    selectionColor: theme.selection,
    labelBackground: theme.labelBackground,
    labelColor: theme.labelText,
  }
}

function toMiniMapTheme(theme: ViewerThemeTokens, options: MiniMapOptions) {
  return {
    background: options.theme?.minimapBackground ?? theme.minimapBackground,
    viewportColor: options.theme?.minimapViewport ?? theme.minimapViewport,
    viewportStroke:
      options.theme?.minimapViewportStroke ?? theme.minimapViewportStroke,
  }
}
